
import React, { useRef, useState } from 'react';
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Globe, ShieldCheck, Cpu, Linkedin, Twitter, Facebook, Instagram, ArrowRight, MousePointer2 } from "lucide-react";
import { Link } from "react-router-dom";

import TextReveal from "@/components/ui/TextReveal";
import GradientBackground from "@/components/ui/GradientBackground";
import { Button } from "@/components/ui/button";

// --- DATA ---
const executives = [
  {
    id: 'ceo',
    name: "Kumbukani Nyirenda",
    title: "Founder & CEO",
    bio: "Driving the vision to digitize logistics for all of Malawi, ensuring trust and efficiency.",
    imageUrl: "https://res.cloudinary.com/unihousingmw/image/upload/v1765895565/WhatsApp_Image_2025-12-16_at_4.29.32_PM_zq6utn.jpg",
    socials: {
      linkedin: "https://www.linkedin.com/in/kumbukani-nathaniel-nyirenda-80b19637a?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app",
      twitter: "https://x.com",
      email: "mailto:kumbukanin209@gmail.com"
    }
  },
  {
    id: 'cto',
    name: "Yohane H. Maluwa",
    title: "Co-Founder & CTO",
    bio: "Architecting the robust, scalable, and secure technology that powers our entire platform.",
    imageUrl: "https://res.cloudinary.com/unihousingmw/image/upload/v1766094424/WhatsApp_Image_2025-12-18_at_12.44.55_PM_thwfnd.jpg",
    socials: {
      linkedin: "https://www.linkedin.com/in/yohane-maluwa-a8274136a",
      twitter: "https://x.com/hanieyo1407",
      facebook: "https://www.facebook.com/yohane.maluwa.56",
      instagram: "https://www.instagram.com/hanieyo1407?igsh=MjB4a3l5MHZ5eGRn",
      email: "mailto:ymaluwa@gmail.com"
    }
  },
  {
    id: 'ops',
    name: "Prince T. Mataka",
    title: "Operations Specialist",
    bio: "Ensuring every shipment runs smoothly from dispatch to delivery, keeping operations reliable and efficient.",
    imageUrl: "https://res.cloudinary.com/unihousingmw/image/upload/v1790258432/WhatsApp_Image_2026-09-20_at_8.56.47_AM_hv8xui.jpg",
    socials: {
      linkedin: "https://www.linkedin.com",
      twitter: "https://x.com",
      email: "mailto:prince@kclogistics265.com"
    }
  }
];

const timeline = [
  { year: "2019", title: "The Spark", desc: "K&C was born in a tiny room with one calculator and a big dream." },
  { year: "2021", title: "The Struggle", desc: "Paperwork hell. Regulatory roadblocks. We almost quit, but reliance kept us moving." },
  { year: "2024", title: "The Rebirth", desc: "Fully compliant, technically reinforced. K&C rises again as a digital-first platform." },
  { year: "2025", title: "Hypergrowth", desc: "Breaking records daily. The most trusted logistics network in Malawi." },
  { year: "2026", title: "Future", desc: "AI-driven autonomous dispatch for the entire SADC region." }
];

const AboutUsPage = () => {
  const [userImage, setUserImage] = useState<string | null>(null);
  const containerRef = useRef(null);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setUserImage(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="bg-background text-foreground overflow-hidden relative min-h-screen font-sans selection:bg-primary/30">
      <GradientBackground />

      {/* Hero Section */}
      <section className="relative pt-40 pb-32 px-4 container mx-auto text-center z-10">
        <div className="max-w-5xl mx-auto space-y-12">
          <div className="inline-block px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 backdrop-blur-md mb-4">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Our Story</span>
          </div>

          <div className="flex flex-col items-center">
            <TextReveal className="text-6xl md:text-9xl font-black tracking-tighter text-foreground leading-[0.9]">
              FROM DUST
            </TextReveal>
            <TextReveal delay={0.1} className="text-6xl md:text-9xl font-black tracking-tighter text-foreground leading-[0.9]">
              TO SKYLINE.
            </TextReveal>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-xl md:text-3xl text-muted-foreground leading-relaxed max-w-3xl mx-auto font-light"
          >
            Driven by passion and growing interest in the transportation sector. Our experience led to the birth of K & C Logistics — a story of grit, setbacks, and a roaring comeback.
          </motion.p>
        </div>
      </section>

      {/* Draggable Timeline Section */}
      <section className="py-24 relative z-10 border-y border-foreground/5 bg-foreground/5 backdrop-blur-sm overflow-hidden">
        <div className="container px-4 mx-auto mb-12 flex justify-between items-end">
          <div>
            <h2 className="text-4xl font-bold tracking-tighter mb-2">Evolution <span className="text-primary">In Motion</span></h2>
            <p className="text-muted-foreground">Drag to explore our history.</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MousePointer2 className="w-4 h-4 animate-bounce" /> <span className="hidden md:inline">Drag</span><span className="md:hidden">Swipe</span>
          </div>
        </div>

        {/* Infinite Drag Slider */}
        <motion.div
          className="flex gap-8 px-4 md:px-20 cursor-grab active:cursor-grabbing"
          drag="x"
          dragConstraints={{ right: 0, left: -(timeline.length * 380 - (typeof window !== 'undefined' ? window.innerWidth : 768) + 80) }}
        >
          {timeline.map((item, i) => (
            <motion.div
              key={i}
              className="shrink-0 w-[350px] md:w-[450px] p-8 md:p-12 rounded-[2.5rem] bg-background border border-foreground/10 hover:border-primary/50 transition-colors relative group select-none"
              whileHover={{ scale: 1.02 }}
            >
              <span className="text-8xl font-black text-foreground/5 absolute -top-4 -right-4 transition-colors group-hover:text-primary/10">{item.year}</span>
              <div className="relative z-10 mt-8">
                <div className="w-3 h-3 rounded-full bg-primary mb-6 ring-4 ring-primary/20" />
                <h4 className="text-3xl font-bold mb-3">{item.title}</h4>
                <p className="text-lg text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Values / Tech - Simplified Grid */}
      <section className="py-32 relative z-10">
        <div className="container px-4 mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: ShieldCheck, title: "Ironclad Trust", desc: "We verify every operator so you never have to worry." },
              { icon: Cpu, title: "Neural Matching", desc: "Our AI finds the perfect truck for your load in milliseconds." },
              { icon: Globe, title: "Total Visibility", desc: "Track every mile. From pickup to payout." }
            ].map((feature, i) => (
              <motion.div
                key={i}
                className="group p-10 bg-foreground/5 rounded-[2rem] border border-transparent hover:border-primary/30 transition-all duration-500 hover:bg-foreground/10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <feature.icon className="w-12 h-12 text-primary mb-6 group-hover:scale-110 transition-transform duration-500" />
                <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed text-lg">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team CTA - Holographic Cards */}
      <section className="py-32 container px-4 mx-auto text-center z-10 relative">
        <div className="flex flex-col items-center mb-24">
          <TextReveal className="text-5xl md:text-8xl font-black tracking-tighter">
            BUILT BY HUMANS.
          </TextReveal>
          <TextReveal delay={0.1} className="text-5xl md:text-8xl font-black tracking-tighter text-primary">
            POWERED BY CODE.
          </TextReveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-24">
          {/* Executive Cards */}
          {executives.map((member, i) => (
            <motion.div
              key={member.id}
              className="group relative overflow-hidden rounded-[2.5rem] aspect-[3/4] bg-foreground/5 isolate"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, type: "spring", stiffness: 50 }}
              viewport={{ once: true }}
            >
              <img src={member.imageUrl} alt={member.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale group-hover:grayscale-0" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent p-10 flex flex-col justify-end text-left">
                <h3 className="text-3xl font-bold text-white mb-1">{member.name}</h3>
                <p className="text-primary font-medium mb-6 text-lg tracking-wide uppercase">{member.title}</p>

                {/* Socials reveal on hover */}
                <div className="flex gap-4 translate-y-8 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                  <a href={member.socials.linkedin} target="_blank" rel="noreferrer" className="text-black bg-white p-3 rounded-full hover:bg-primary hover:scale-110 transition-all">
                    <Linkedin size={20} />
                  </a>
                  <a href={member.socials.email} className="text-black bg-white p-3 rounded-full hover:bg-primary hover:scale-110 transition-all">
                    <div className="w-5 h-5 flex items-center justify-center font-bold">@</div>
                  </a>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Interactive "Join Team" Card */}
          <motion.div
            className="group relative overflow-hidden rounded-[2.5rem] aspect-[3/4] border-2 border-dashed border-foreground/20 hover:border-primary/50 transition-colors cursor-pointer bg-foreground/5 flex items-center justify-center p-8"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
          >
            <input
              type="file"
              accept="image/*"
              className="absolute inset-0 opacity-0 z-20 cursor-pointer w-full h-full"
              onChange={handleImageUpload}
              title="Upload your photo"
            />

            {userImage ? (
              <img src={userImage} alt="You" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all" />
            ) : (
              <div className="text-center">
                <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300">
                  <span className="text-5xl text-primary">+</span>
                </div>
                <h3 className="text-3xl font-bold text-foreground mb-2">You?</h3>
                <p className="text-muted-foreground">Click to upload your photo.</p>
              </div>
            )}

            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent p-10 flex flex-col justify-end text-left pointer-events-none z-10">
              <h3 className="text-3xl font-bold text-white">{userImage ? "Future Legend" : "Join Us"}</h3>
              <p className="text-primary font-medium tracking-wide uppercase">Your Role Here</p>
            </div>
          </motion.div>
        </div>

        <div className="flex justify-center gap-6">
          <Button asChild size="xl" className="rounded-full text-xl px-10 h-16 shadow-2xl hover:scale-105 transition-transform">
            <Link to="/contact">Get in Touch <ArrowRight className="ml-2 w-6 h-6" /></Link>
          </Button>
        </div>
      </section>

    </div>
  );
};

export default AboutUsPage;
